// Pure helpers for the DeskNotes panel's multi-location layout.
//
// A desk token covers one, some, or all centers (backend lib/desk-scope.js).
// Single-center panels render as before; multi-center panels stack one
// coloured section per location (the ops location-picker colours), and
// All-locations panels add filter chips. Everything here is data-in /
// data-out so it can be unit-tested without React.
//
// Spec: docs/superpowers/specs/2026-10-09-desk-token-locations-design.md

export const OTHER_KEY = 'other';

export function deviceScope(device) {
  if (device?.allCenters === true) return { allCenters: true, centerIds: [] };
  const list = Array.isArray(device?.centerIds) ? device.centerIds.filter(Boolean) : [];
  if (list.length > 0) return { allCenters: false, centerIds: list };
  return { allCenters: false, centerIds: device?.centerId ? [device.centerId] : [] };
}

export function isMulti(device) {
  const s = deviceScope(device);
  return s.allCenters || s.centerIds.length > 1;
}

function emptySection(loc) {
  return {
    key: loc.key, centerId: loc.centerId || null, label: loc.label, short: loc.short,
    color: loc.color, bg: loc.bg, kiosk: [], rest: [], applying: [], autoApplied: [], openCount: 0,
  };
}

const OTHER_LOC = { key: OTHER_KEY, label: 'Other', short: '?', color: '#6B7280', bg: '#F3F4F6', centerId: null };

/**
 * rows → ordered sections. `locations` is the ops LOCATIONS list (entries
 * without centerId, like "All", are ignored). Some-tokens get only their
 * centers; All-tokens get every known location. A row whose center is not
 * in the section list goes to a trailing "Other" section (never dropped).
 */
export function buildSections(rows, device, locations) {
  const scope = deviceScope(device);
  const known = (locations || []).filter((l) => l.centerId);
  const wanted = scope.allCenters ? known : known.filter((l) => scope.centerIds.includes(l.centerId));
  const sections = wanted.map(emptySection);
  const byCenter = new Map(sections.map((s) => [s.centerId, s]));
  let other = null;

  for (const r of rows || []) {
    let sec = byCenter.get(r.centerId);
    if (!sec) {
      if (!other) other = emptySection(OTHER_LOC);
      sec = other;
    }
    if (r.status === 'open') {
      if (r.kioskWaitingAt) sec.kiosk.push(r); else sec.rest.push(r);
      sec.openCount += 1;
    } else if (r.status === 'applying') {
      sec.applying.push(r);
    } else if (r.status === 'auto_applied') {
      sec.autoApplied.push(r);
    }
    // done / cant / expired live in the shared "Done today" history, not in sections.
  }
  return other ? [...sections, other] : sections;
}

/** All-token filter chip: never let a new request hide behind the current chip. */
export function nextFilter(current, newCenterId) {
  if (current === 'all' || current === newCenterId) return current;
  return newCenterId;
}
